import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { toast } from 'react-toastify';

import { fetchMenu } from '../modules/menu/menuApi';
import { fetchUserDetails, logout } from '../modules/user/profileSlice';
import { API_CONSTANTS } from '../utils/constants';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_CONSTANTS.BASE_URL,
  prepareHeaders: (headers, api) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const requestArg = (api as { arg?: string | FetchArgs }).arg;
    const requestBody =
      requestArg && typeof requestArg === 'object' && 'body' in requestArg
        ? (requestArg as FetchArgs).body
        : undefined;
    const isMultipart = requestBody instanceof FormData;

    if (isMultipart) {
      headers.delete('Content-Type');
    } else if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    return headers;
  },
});

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    const refreshResult = await rawBaseQuery(
      {
        url: API_CONSTANTS.REFRESH_TOKEN_ENDPOINT,
        method: 'POST',
      },
      api,
      extraOptions
    );

    const accessToken = (
      refreshResult.data as { data?: { access_token?: string } }
    )?.data?.access_token;

    if (accessToken) {
      localStorage.setItem('access_token', accessToken);
      await api.dispatch(fetchUserDetails());
      await api.dispatch(fetchMenu());
      result = await rawBaseQuery(args, api, extraOptions);
    } else {
      // If refresh fails or returns unauthorized, log out the user
      api.dispatch(logout());
    }
  }

  // Handle Global Toast Logic
  if (result.data) {
    const data = result.data as { success?: boolean; message?: string };
    const method = (args as FetchArgs).method || 'GET';

    // Show success toast for mutations (POST, PUT, DELETE) if success is true and message exists
    const showSuccessToast = (args as any).showSuccessToast !== false;
    if (data.success && data.message && method !== 'GET' && showSuccessToast) {
      toast.success(data.message);
    }
    // Handle cases where success might be false but it's returned as 200 OK
    else if (data.success === false && data.message) {
      toast.error(data.message);
    }
  } else if (result.error) {
    // Handle error toasts for failed requests
    const errorData = result.error.data as { message?: string };
    if (errorData?.message) {
      toast.error(errorData.message);
    } else {
      // toast.error('An unexpected error occurred'); // Optional: Generic error fallback
    }
  }

  return result;
};
